import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { AuthMe } from '@horology/shared-types';
import { api } from '../lib/api';
import { supabase } from '../lib/supabase';

export function useSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setLoading(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

export function useMe() {
  const { session, loading } = useSession();
  return useQuery({
    queryKey: ['auth', 'me', session?.access_token ?? 'anon'],
    enabled: !loading && !!session,
    queryFn: async (): Promise<AuthMe> => {
      const { data } = await api.get<AuthMe>('/api/me');
      return data;
    },
    staleTime: 60_000,
  });
}

export function hasPermission(me: AuthMe | undefined, code: string): boolean {
  if (!me) return false;
  if (me.permissions.includes('*')) return true;
  return me.permissions.includes(code);
}
